import React, { useState } from 'react';
import { LiquidityEvent } from '../types';
import { formatCurrency, futureDate } from '../utils/currency';
import { Button } from './ui/Button';
import { Input } from './ui/Forms';
import { Trash2, ArrowUpRight, ArrowDownRight, Calendar, Repeat, Pencil, Check, X, Power, Edit2 } from 'lucide-react';
interface LiquidityEventListProps {
  events: LiquidityEvent[];
  onDelete: (id: string) => void;
  onUpdate?: (id: string, updates: Partial<Omit<LiquidityEvent, 'id'>>) => void;
  onToggleActive?: (id: string) => void;
  onExport?: () => void;
}
{}
function EventRow({
  ev,
  onDelete,
  onUpdate,
  onToggleActive
}: {
  ev: LiquidityEvent;
  onDelete: (id: string) => void;
  onUpdate?: (id: string, updates: Partial<Omit<LiquidityEvent, 'id'>>) => void;
  onToggleActive?: (id: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(ev.name);
  const [editAmount, setEditAmount] = useState(Math.abs(ev.amount).toString());
  const [editStartYear, setEditStartYear] = useState(ev.startYear.toString());
  const [editEndYear, setEditEndYear] = useState(ev.endYear?.toString() || '');
  const isPositive = ev.amount >= 0;
  const isActive = ev.isActive ?? true;
  const handleSave = () => {
    if (!onUpdate) return;
    const amountVal = Number(editAmount);
    const startYearVal = Number(editStartYear);
    const endYearVal = editEndYear ? Number(editEndYear) : undefined;
    if (editName.trim() && !isNaN(amountVal) && !isNaN(startYearVal)) {
      onUpdate(ev.id, {
        name: editName.trim(),
        amount: isPositive ? amountVal : -amountVal,
        startYear: startYearVal,
        endYear: endYearVal
      });
      setIsEditing(false);
    }
  };
  const handleCancel = () => {
    setEditName(ev.name);
    setEditAmount(Math.abs(ev.amount).toString());
    setEditStartYear(ev.startYear.toString());
    setEditEndYear(ev.endYear?.toString() || '');
    setIsEditing(false);
  };
  if (isEditing) {
    return <tr className="flex flex-col md:table-row border-b border-slate-100 last:border-0 bg-slate-50/50 p-4 md:p-0" data-id="element-737">
        <td className="block md:table-cell px-0 py-2 md:px-4 md:py-4" data-id="element-738">
          <Input className="h-8 text-sm" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Event name" autoFocus data-id="element-739" />
        </td>
        <td className="block md:table-cell px-0 py-2 md:px-4 md:py-4" data-id="element-740">
          <div className="flex items-center gap-2" data-id="element-741">
            <div className="flex items-center gap-1" data-id="element-742">
              <span className="text-xs text-slate-500" data-id="element-743">Yr</span>
              <Input type="number" className="w-16 h-8 text-sm px-2" value={editStartYear} onChange={e => setEditStartYear(e.target.value)} data-id="element-744" />
            </div>
            {ev.type !== 'one-time' && <>
                <span className="text-xs text-slate-500" data-id="element-745">to</span>
                <Input type="number" className="w-16 h-8 text-sm px-2" value={editEndYear} onChange={e => setEditEndYear(e.target.value)} placeholder="∞" data-id="element-746" />
              </>}
          </div>
        </td>
        <td className="block md:table-cell px-0 py-2 md:px-4 md:py-4" data-id="element-747">
          <div className="flex items-center justify-start md:justify-end gap-1" data-id="element-748">
            <span className={isPositive ? 'text-emerald-600' : 'text-rose-600'} data-id="element-749">
              {isPositive ? '+' : '-'}
            </span>
            <Input type="number" min="0" step="0.01" className="w-24 h-8 text-right px-2 py-1 text-sm" value={editAmount} onChange={e => setEditAmount(e.target.value)} data-id="element-750" />
          </div>
        </td>
        <td className="block md:table-cell px-0 py-2 md:px-4 md:py-4 text-left md:text-center" data-id="element-751">
          <div className="flex items-center justify-start md:justify-center gap-1" data-id="element-752">
            <Button variant="ghost" size="sm" onClick={handleSave} className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-2" data-id="element-753">
              <Check className="w-4 h-4" data-id="element-754" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCancel} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 px-2" data-id="element-755">
              <X className="w-4 h-4" data-id="element-756" />
            </Button>
          </div>
        </td>
      </tr>;
  }
  return <>
      {/* Mobile View */}
      <tr className={`md:hidden block border-b border-slate-100 p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-757">
        <td className="block p-0" data-id="element-758">
          <div className="flex flex-col gap-3" data-id="element-759">
            <div className="flex justify-between items-start gap-4" data-id="element-760">
              <div className="flex flex-col" data-id="element-761">
                <div className="flex items-center gap-2" data-id="element-762">
                  <div className={`p-1.5 rounded-md ${isPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-763">
                    {isPositive ? <ArrowUpRight className="w-3.5 h-3.5" data-id="element-764" /> : <ArrowDownRight className="w-3.5 h-3.5" data-id="element-765" />}
                  </div>
                  <span className="font-medium text-slate-900" data-id="element-766">{ev.name}</span>
                  {!isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-767">
                      INACTIVE
                    </span>}
                </div>
                <span className="text-xs text-slate-500 mt-1 ml-8" data-id="element-768">
                  {ev.type === 'one-time' ? 'One-time' : ev.type === 'monthly' ? 'Monthly' : 'Yearly'}{' '}
                  · {ev.currency}
                </span>
              </div>
              <div className="flex flex-col items-end" data-id="element-769">
                <span className={`font-semibold ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-770">
                  {isPositive ? '+' : ''}
                  {formatCurrency(ev.amount, ev.currency)}
                </span>
                {ev.type !== 'one-time' && <span className="text-xs text-slate-400 mt-0.5" data-id="element-771">
                    /{ev.type === 'monthly' ? 'mo' : 'yr'}
                  </span>}
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-slate-50" data-id="element-772">
              <div className="flex items-center gap-1.5" data-id="element-773">
                {ev.type === 'one-time' ? <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 gap-1.5" data-id="element-774">
                    Yr {ev.startYear}
                    <span className="text-blue-400 font-normal" data-id="element-775">
                      ({futureDate(ev.startYear)})
                    </span>
                  </span> : <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 gap-1" data-id="element-776">
                    <Repeat className="w-3 h-3" data-id="element-777" />
                    Yr {ev.startYear}
                    <span className="text-purple-400 font-normal" data-id="element-778">
                      {ev.endYear ? `→ Yr ${ev.endYear}` : '→ ∞'}
                    </span>
                  </span>}
              </div>
              <div className="flex items-center gap-1" data-id="element-779">
                {onToggleActive && <Button variant="ghost" size="sm" onClick={() => onToggleActive(ev.id)} className="px-2 text-slate-400" data-id="element-780">
                    <Power className="w-4 h-4" data-id="element-781" />
                  </Button>}
                {onUpdate && <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="px-2 text-slate-400" data-id="element-782">
                    <Edit2 className="w-4 h-4" data-id="element-783" />
                  </Button>}
                <Button variant="ghost" size="sm" onClick={() => onDelete(ev.id)} className="px-2 text-slate-400" data-id="element-784">
                  <Trash2 className="w-4 h-4" data-id="element-785" />
                </Button>
              </div>
            </div>
          </div>
        </td>
      </tr>

      {/* Desktop View */}
      <tr className={`hidden md:table-row hover:bg-slate-50/50 transition-colors group border-b border-slate-100 last:border-0 ${!isActive ? 'opacity-50' : ''}`} data-id="element-786">
        <td className="px-4 py-4 font-medium text-slate-900" data-id="element-787">
          <div className="flex flex-col" data-id="element-788">
            <div className="flex items-center gap-2" data-id="element-789">
              <div className={`p-1.5 rounded-md ${isPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-790">
                {isPositive ? <ArrowUpRight className="w-3.5 h-3.5" data-id="element-791" /> : <ArrowDownRight className="w-3.5 h-3.5" data-id="element-792" />}
              </div>
              <span data-id="element-793">{ev.name}</span>
              {!isActive && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-794">
                  INACTIVE
                </span>}
            </div>
            <span className="text-xs text-slate-400 font-normal mt-0.5 ml-8" data-id="element-795">
              {ev.type === 'one-time' ? 'One-time' : ev.type === 'monthly' ? 'Monthly' : 'Yearly'}{' '}
              · {ev.currency}
            </span>
          </div>
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-796">
          <div className="flex items-center gap-1.5" data-id="element-797">
            {ev.type === 'one-time' ? <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 gap-1.5" data-id="element-798">
                Yr {ev.startYear}
                <span className="text-blue-400 font-normal" data-id="element-799">
                  ({futureDate(ev.startYear)})
                </span>
              </span> : <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 gap-1" data-id="element-800">
                <Repeat className="w-3 h-3" data-id="element-801" />
                Yr {ev.startYear}
                <span className="text-purple-400 font-normal" data-id="element-802">
                  {ev.endYear ? `→ Yr ${ev.endYear}` : '→ ∞'}
                </span>
              </span>}
          </div>
        </td>
        <td className="px-4 py-4 text-right font-medium" data-id="element-803">
          <span className={isPositive ? 'text-emerald-600' : 'text-rose-600'} data-id="element-804">
            {isPositive ? '+' : ''}
            {formatCurrency(ev.amount, ev.currency)}
          </span>
          {ev.type !== 'one-time' && <span className="text-xs text-slate-400 font-normal ml-1" data-id="element-805">
              /{ev.type === 'monthly' ? 'mo' : 'yr'}
            </span>}
        </td>
        <td className="px-4 py-4 text-center" data-id="element-806">
          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-807">
            {onToggleActive && <Button variant="ghost" size="sm" onClick={() => onToggleActive(ev.id)} className={`px-2 ${isActive ? 'text-slate-400 hover:text-amber-600' : 'text-amber-500 hover:text-emerald-600 !opacity-100'}`} title={isActive ? 'Deactivate' : 'Reactivate'} data-id="element-808">
                <Power className="w-4 h-4" data-id="element-809" />
              </Button>}
            {onUpdate && <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="text-slate-400 hover:text-indigo-600 px-2" title="Edit" data-id="element-810">
                <Edit2 className="w-4 h-4" data-id="element-811" />
              </Button>}
            <Button variant="ghost" size="sm" onClick={() => onDelete(ev.id)} className="text-slate-400 hover:text-red-600 px-2" title="Delete" data-id="element-812">
              <Trash2 className="w-4 h-4" data-id="element-813" />
            </Button>
          </div>
        </td>
      </tr>
    </>;
}
export function LiquidityEventList({
  events,
  onDelete,
  onUpdate,
  onToggleActive,
  onExport
}: LiquidityEventListProps) {
  return <div className="h-full flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-814">
      {events.length === 0 ? <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-full min-h-[300px]" data-id="element-815">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3" data-id="element-816">
            <Calendar className="w-6 h-6 text-slate-400" data-id="element-817" />
          </div>
          <p data-id="element-818">No cash events added yet.</p>
          <p className="text-sm mt-1" data-id="element-819">
            Add future inflows or outflows to improve projection accuracy.
          </p>
        </div> : <>
          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-slate-100" data-id="element-820">
            {events.map(ev => {
          const isPositive = ev.amount >= 0;
          const isActive = ev.isActive ?? true;
          return <div key={ev.id} className={`p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-821">
                  <div className="flex items-start justify-between mb-2" data-id="element-822">
                    <div className="flex items-center gap-2 min-w-0 flex-1 mr-3" data-id="element-823">
                      <div className={`p-1.5 rounded-md shrink-0 ${isPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-824">
                        {isPositive ? <ArrowUpRight className="w-3.5 h-3.5" data-id="element-825" /> : <ArrowDownRight className="w-3.5 h-3.5" data-id="element-826" />}
                      </div>
                      <div className="min-w-0" data-id="element-827">
                        <p className="font-medium text-slate-900 truncate" data-id="element-828">
                          {ev.name}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-829">
                          {ev.type === 'one-time' ? 'One-time' : ev.type === 'monthly' ? 'Monthly' : 'Yearly'}{' '}
                          · {ev.currency}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0" data-id="element-830">
                      <p className={`font-semibold ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-831">
                        {isPositive ? '+' : ''}
                        {formatCurrency(ev.amount, ev.currency)}
                      </p>
                      {ev.type !== 'one-time' && <p className="text-xs text-slate-400" data-id="element-832">
                          /{ev.type === 'monthly' ? 'mo' : 'yr'}
                        </p>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2" data-id="element-833">
                    <div data-id="element-834">
                      {ev.type === 'one-time' ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700" data-id="element-835">
                          Yr {ev.startYear}
                        </span> : <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 gap-1" data-id="element-836">
                          <Repeat className="w-3 h-3" data-id="element-837" />
                          Yr {ev.startYear}{' '}
                          {ev.endYear ? `→ ${ev.endYear}` : '→ ∞'}
                        </span>}
                      {!isActive && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-838">
                          INACTIVE
                        </span>}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-839">
                      {onToggleActive && <Button variant="ghost" size="sm" onClick={() => onToggleActive(ev.id)} className={`px-2 ${isActive ? 'text-slate-400' : 'text-amber-500'}`} data-id="element-840">
                          <Power className="w-4 h-4" data-id="element-841" />
                        </Button>}
                      {onUpdate && <Button variant="ghost" size="sm" className="text-slate-400 px-2" data-id="element-842">
                          <Edit2 className="w-4 h-4" data-id="element-843" />
                        </Button>}
                      <Button variant="ghost" size="sm" onClick={() => onDelete(ev.id)} className="text-slate-400 px-2" data-id="element-844">
                        <Trash2 className="w-4 h-4" data-id="element-845" />
                      </Button>
                    </div>
                  </div>
                </div>;
        })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto" data-id="element-846">
            <table className="w-full text-sm text-left" data-id="element-847">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-848">
                <tr data-id="element-849">
                  <th className="px-4 py-3 font-medium" data-id="element-850">Event</th>
                  <th className="px-4 py-3 font-medium" data-id="element-851">Timing</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-852">Amount</th>
                  <th className="px-4 py-3 font-medium text-center" data-id="element-853">Actions</th>
                </tr>
              </thead>
              <tbody data-id="element-854">
                {events.map(ev => <EventRow key={ev.id} ev={ev} onDelete={onDelete} onUpdate={onUpdate} onToggleActive={onToggleActive} data-id="element-855" />)}
              </tbody>
            </table>
          </div>
        </>}
    </div>;
}